import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      // Ignore if any modifier key is pressed (Cmd, Ctrl, Alt, Shift)
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;

      switch (e.key.toLowerCase()) {
        case 'p':
          navigate('/products');
          break;
        case 'b':
          navigate('/banners');
          break;
        case 'f':
          navigate('/live-filters');
          break;
        case 'g':
          navigate('/live-backgrounds');
          break;
        case 's':
          navigate('/live-stickers');
          break;
        case 'a':
          navigate('/gift-animations');
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  // PingTop brand gradient
  const brandGradient = 'from-[#FF9C15] to-[#F42026]';

  /**
   * Every card here corresponds to something reachable from the navbar (see Navbar.tsx's
   * STANDALONE/GROUPS) — kept as a parallel list rather than importing that structure directly
   * because the navbar's entries are optimized for compact nav labels while these need a fuller
   * description, icon size and stat line. When adding a destination, add it in BOTH places: a
   * navbar entry with no dashboard card is unreachable from the homepage; a card with no navbar
   * entry means there is no way back to it once you leave the dashboard.
   */
  type Section = {
    heading: string;
    items: Array<{
      title: string;
      description: string;
      icon: JSX.Element;
      path: string;
      stats: string;
    }>;
  };

  const sections: Section[] = [
    {
      heading: 'Wallet & Content',
      items: [
        {
          title: 'Wallet Shop Catalog',
          description: 'View and manage wallet shop products, pricing, and availability.',
          icon: (
            <svg className='w-12 h-12' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M3 7h18M5 7l1 12a2 2 0 002 2h8a2 2 0 002-2l1-12' />
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M9 11h6' />
            </svg>
          ),
          path: '/products',
          stats: 'Products & pricing',
        },
        {
          title: 'Banner Management',
          description: 'Create and manage promotional banners for different screens.',
          icon: (
            <svg className='w-12 h-12' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1.5}
                d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
              />
            </svg>
          ),
          path: '/banners',
          stats: 'Screen banners',
        },
        {
          title: 'Asset Uploads',
          description: 'Generate presigned URLs and attach assets to wallet shop products.',
          icon: (
            <svg className='w-12 h-12' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2' />
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M7 10l5-5 5 5M12 5v13' />
            </svg>
          ),
          path: '/products',
          stats: 'Presigned uploads',
        },
        {
          title: 'Announcements',
          description: 'Publish and manage in-app announcements and their attached assets.',
          icon: (
            <svg className='w-12 h-12' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1.5}
                d='M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z'
              />
            </svg>
          ),
          path: '/announcements',
          stats: 'In-app announcements',
        },
        {
          title: 'Gift Animations',
          description: 'Upload and manage Lottie JSON animations attached to token gifts.',
          icon: (
            <svg className='w-12 h-12' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z' />
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
            </svg>
          ),
          path: '/gift-animations',
          stats: 'Lottie animations',
        },
        {
          title: 'Entry Animations',
          description: 'Upload iOS (.mov) and Android (.webm) join-celebration animations users purchase and equip.',
          icon: (
            <svg className='w-12 h-12' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M13 7l5 5m0 0l-5 5m5-5H6' />
            </svg>
          ),
          path: '/products?category=LIVE',
          stats: 'Live · token-priced',
        },
        {
          title: 'Premium Packs',
          description: 'Bundle a frame, background, chat theme, sticker pack and entry animation into one Themes & Decor purchase.',
          icon: (
            <svg className='w-12 h-12' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4' />
            </svg>
          ),
          path: '/products?category=THEMES_DECOR',
          stats: 'Bundles & sticker packs',
        },
        {
          title: 'Bulk Frame Upload',
          description: 'Bulk upload profile frames or animated frames to the wallet shop catalog.',
          icon: (
            <svg className='w-12 h-12' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M4 4h16v16H4V4zm3 3h10v10H7V7z' />
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M12 9v6m-3-3h6' />
            </svg>
          ),
          path: '/products/frames/bulk',
          stats: 'Bulk frames',
        },
      ],
    },
    {
      heading: 'Live Assets',
      items: [
        {
          title: 'Live Filters',
          description: 'Upload and manage DeepAR filters available for live streams.',
          icon: (
            <svg className='w-12 h-12' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1.5}
                d='M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
          ),
          path: '/live-filters',
          stats: '.deepar filters',
        },
        {
          title: 'Live Backgrounds',
          description: 'Upload and manage JPEG background images available for live streams.',
          icon: (
            <svg className='w-12 h-12' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1.5}
                d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
              />
            </svg>
          ),
          path: '/live-backgrounds',
          stats: 'JPEG backgrounds',
        },
        {
          title: 'Live Stickers',
          description: 'Curate the sticker packs and writable text stickers hosts can place on their video.',
          icon: (
            <svg className='w-12 h-12' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z' />
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M12 3v6h6' />
            </svg>
          ),
          path: '/live-stickers',
          stats: 'PNG / WebP packs',
        },
        {
          title: 'Live Frames',
          description: 'Upload transparent animated borders hosts can apply to their live session.',
          icon: (
            <svg className='w-12 h-12' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M4 4h16v16H4V4zm3 3h10v10H7V7z' />
            </svg>
          ),
          path: '/live-frames',
          stats: '.mov / .webm / .webp',
        },
        {
          title: 'Live Milestone Badges',
          description: 'Upload iOS (.mov) and Android (.webm) celebration animations for livestream like milestones.',
          icon: (
            <svg className='w-12 h-12' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z' />
            </svg>
          ),
          path: '/live-milestone-badges',
          stats: 'Bronze / Silver / Gold / Diamond',
        },
        {
          title: 'Live Quest Assets',
          description: 'Upload reward-track artwork for all 50 Basic and 50 Plus levels.',
          icon: (
            <svg className='w-12 h-12' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M12 2l2.7 5.47 6.04.88-4.37 4.26 1.03 6.02L12 16.77 6.6 19.63l1.03-6.02L3.26 9.35l6.04-.88L12 2z' />
            </svg>
          ),
          path: '/live-quest-assets',
          stats: 'Basic + Plus reward tracks',
        },
      ],
    },
  ];

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />

      {/* Hero Section */}
      <div className='relative overflow-hidden'>
        {/* Background decoration */}
        <div className='absolute inset-0 overflow-hidden pointer-events-none'>
          <div className='absolute -top-40 -right-40 w-80 h-80 bg-[#FF9C15]/10 rounded-full blur-3xl' />
          <div className='absolute -bottom-40 -left-40 w-80 h-80 bg-[#F42026]/10 rounded-full blur-3xl' />
          <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#FF9C15]/5 rounded-full blur-3xl' />
        </div>

        <div className='relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20'>
          {/* Header */}
          <div className='text-center mb-12 sm:mb-16'>
            <div className='inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-sm rounded-full border border-white/10 mb-6'>
              <div className='w-2 h-2 bg-green-400 rounded-full animate-pulse' />
              <span className='text-sm text-gray-300'>Wallet Assets Console</span>
            </div>
            <h1 className='text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4'>
              <span className='bg-gradient-to-r from-[#FF9C15] to-[#F42026] bg-clip-text text-transparent'>
                Tezda
              </span>{' '}
              Assets
            </h1>
            <p className='text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto'>
              Manage wallet shop products, media assets, and catalog availability in one place
            </p>
          </div>

          {/* Menu Cards, grouped to mirror the navbar's dropdown sections */}
          {sections.map((section) => (
            <div key={section.heading} className='mb-12 sm:mb-16 last:mb-0'>
              <h2 className='text-sm font-semibold uppercase tracking-wider text-gray-500 mb-5 px-1'>
                {section.heading}
              </h2>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8'>
                {section.items.map((item) => (
                  <button
                    key={`${item.title}-${item.path}`}
                    onClick={() => navigate(item.path)}
                    className='group relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 sm:p-8 text-left transition-all duration-300 hover:bg-white/10 hover:border-[#FF9C15]/30 hover:scale-[1.02] hover:shadow-2xl hover:shadow-[#F42026]/10'
                  >
                    {/* Gradient overlay on hover */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${brandGradient} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-300`} />

                    {/* Icon */}
                    <div className={`inline-flex p-4 bg-gradient-to-br ${brandGradient} rounded-xl text-white mb-6 shadow-lg`}>
                      {item.icon}
                    </div>

                    {/* Content */}
                    <h3 className='text-xl sm:text-2xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-[#FF9C15] group-hover:to-[#F42026] transition-all duration-300'>
                      {item.title}
                    </h3>
                    <p className='text-gray-400 text-sm sm:text-base mb-4 leading-relaxed'>{item.description}</p>

                    {/* Stats badge */}
                    <div className='inline-flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-full'>
                      <div className={`w-1.5 h-1.5 bg-gradient-to-r ${brandGradient} rounded-full`} />
                      <span className='text-xs text-gray-400'>{item.stats}</span>
                    </div>

                    {/* Arrow indicator */}
                    <div className='absolute bottom-6 right-6 sm:bottom-8 sm:right-8 opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0 transition-all duration-300'>
                      <svg className='w-6 h-6 text-[#FF9C15]' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M17 8l4 4m0 0l-4 4m4-4H3' />
                      </svg>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}

          {/* Quick Stats / Info */}
          <div className='mt-4 grid grid-cols-2 md:grid-cols-4 gap-4'>
            {[
              { label: 'Products', value: 'Catalog', icon: '🛍️' },
              { label: 'Uploads', value: 'R2', icon: '☁️' },
              { label: 'Pricing', value: 'Coins', icon: '🪙' },
              { label: 'Status', value: 'Online', icon: '🟢' },
            ].map((stat) => (
              <div key={stat.label} className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-center'>
                <div className='text-2xl mb-2'>{stat.icon}</div>
                <div className='text-lg font-semibold text-white'>{stat.value}</div>
                <div className='text-xs text-gray-500'>{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Keyboard shortcuts hint (desktop only) */}
          <div className='hidden lg:block mt-12 text-center'>
            <div className='inline-flex flex-wrap items-center justify-center gap-4 px-6 py-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full'>
              <span className='text-sm text-gray-400'>Quick navigation:</span>
              <div className='flex items-center gap-2'>
                <kbd className='px-2 py-1 bg-white/10 rounded text-xs text-gray-300 font-mono'>P</kbd>
                <span className='text-xs text-gray-500'>Products</span>
              </div>
              <div className='flex items-center gap-2'>
                <kbd className='px-2 py-1 bg-white/10 rounded text-xs text-gray-300 font-mono'>B</kbd>
                <span className='text-xs text-gray-500'>Banners</span>
              </div>
              <div className='flex items-center gap-2'>
                <kbd className='px-2 py-1 bg-white/10 rounded text-xs text-gray-300 font-mono'>F</kbd>
                <span className='text-xs text-gray-500'>Filters</span>
              </div>
              <div className='flex items-center gap-2'>
                <kbd className='px-2 py-1 bg-white/10 rounded text-xs text-gray-300 font-mono'>G</kbd>
                <span className='text-xs text-gray-500'>Backgrounds</span>
              </div>
              <div className='flex items-center gap-2'>
                <kbd className='px-2 py-1 bg-white/10 rounded text-xs text-gray-300 font-mono'>S</kbd>
                <span className='text-xs text-gray-500'>Stickers</span>
              </div>
              <div className='flex items-center gap-2'>
                <kbd className='px-2 py-1 bg-white/10 rounded text-xs text-gray-300 font-mono'>A</kbd>
                <span className='text-xs text-gray-500'>Animations</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
